import Layout from '@comp/layout'
import SolutionMeta from '@comp/solution/solution-meta'
import SolutionHero from '@comp/solution/solution-hero'
import SolutionPillars, { Pillar } from '@comp/solution/solution-pillars'
import ProductHowItWorks, { Step } from '@comp/product/product-how-it-works'
import SolutionRelated, { RelatedItem } from '@comp/solution/solution-related'
import FinalCta from '@comp/home/final-cta'

const pillars: Pillar[] = [
  {
    title: 'Vault every privileged secret',
    description:
      'Service accounts, root credentials, database passwords, API keys, certificates, SSH keys: one vault, one policy surface.',
    bullets: [
      'Linux / Windows / Network device credentials',
      'Database accounts (Oracle, MSSQL, PostgreSQL, MySQL)',
      'Cloud IAM access keys (AWS, Azure, GCP)',
      'API keys, SSH keys, and certificates',
    ],
  },
  {
    title: 'Automatic rotation, no service outages',
    description:
      'Rotate on a schedule, after every use, or on demand. Synchronized rotation across dependent services prevents outages.',
    bullets: [
      'Scheduled rotation by sensitivity tier',
      'On-checkout and on-checkin rotation',
      'Linked-account synchronization',
      'Pre-rotation validation hooks',
    ],
  },
  {
    title: 'Brokered access, no human ever sees the secret',
    description:
      'Users request a session, not a password. Monopam injects the credential at session start; users authenticate without ever seeing it.',
    bullets: [
      'Zero-display credential injection',
      'Native client passthrough (RDP, SSH, DB clients)',
      'Web app credential auto-fill',
      'Time-bound check-out with auto-revoke',
    ],
  },
  {
    title: 'Encrypted at rest, encrypted in transit, sealed',
    description:
      'AES-256 storage, TLS in transit, HSM-backed master key. Sealed mode requires multi-operator unsealing on cold start.',
    bullets: [
      'AES-256 at rest, TLS 1.2+ in transit',
      'HSM and KMS integration',
      'Shamir-style sealed startup',
      'Per-secret access logs and approvals',
    ],
  },
]

const steps: Step[] = [
  {
    title: 'Onboard secrets',
    description:
      'Import existing credentials via CSV, API, or auto-discovery against your directories and infrastructure.',
  },
  {
    title: 'Set rotation and policy',
    description:
      'Define rotation frequency, approval requirements, and check-out duration per credential type.',
  },
  {
    title: 'Broker every access',
    description:
      'Users request sessions; Monopam injects credentials, records the session, and revokes on close.',
  },
]

const related: RelatedItem[] = [
  {
    label: 'Use case: machine identity',
    description:
      'Certificates, keys, and service accounts as one discipline.',
    href: '/solutions/machine-identity',
  },
  {
    label: 'Certificate Manager',
    description:
      'Certificates discovered, renewed, and deployed, agentlessly.',
    href: '/pam/certificate-manager',
  },
  {
    label: 'Just-in-time access',
    description:
      'Time-bound, approval-driven privileged access.',
    href: '/pam/jit-access',
  },
]

export default function VaultPage() {
  return (
    <Layout>
      <SolutionMeta
        title="Privileged credential vault"
        description="Vault every privileged secret. Automatic rotation, brokered access, encrypted at rest, HSM-backed. Users authenticate without ever seeing the credential."
        path="/pam/vault"
      />
      <main className="bg-surface text-ink-primary">
        <SolutionHero
          eyebrow="Monopam · Credential vault"
          title={
            <>
              Privileged secrets,
              <br />
              never in human hands.
            </>
          }
          body="One vault for every service account, root password, database credential, and cloud key. Automatic rotation, brokered access, and encrypted from disk to wire; users sign in to systems without ever seeing the secret."
          badges={[
            'AES-256 at rest, TLS in transit',
            'Automatic rotation',
            'HSM / KMS backed',
          ]}
        />
        <SolutionPillars
          title={<>Vault, rotate, broker: all in one.</>}
          body="Monopam treats the vault as the start of a brokered session, not a password lookup tool. Secrets enter; humans never carry them out."
          pillars={pillars}
        />
        <ProductHowItWorks
          title={<>Three steps to a brokered privileged stack.</>}
          steps={steps}
        />
        <SolutionRelated items={related} />
        <FinalCta />
      </main>
    </Layout>
  )
}
